import { useEffect, useRef, useState } from "react";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getDatabase, onValue, ref } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { db } from "../../../../../firebase";
import { managedOffersRequest, normalizeOffers, matchesAssignment } from "../../data/managedOffersApi";

const errorMessage = (error) => error?.response?.data?.message || "No se pudo consultar el gestor de ofertas. Intenta actualizar.";

export default function useManagedBookingOffers(booking, token, actorId) {
  const bookingId = booking?.uuid;
  const [remote, setRemote] = useState({});
  const [tick, setTick] = useState(0);
  const controller = useRef(null);
  const current = remote.bookingId === bookingId && remote.actorId === actorId ? remote : {};

  useEffect(() => {
    if (!bookingId || !token || !actorId) return undefined;
    let disposed = false;
    let snapshot = null;
    let anchor = null;
    let loading = true;
    let busy = false;
    let error = "";
    let pending = null;
    let reading = false;
    let recovering = false;
    let readApp;
    const listeners = [];
    const storageKey = `business-offer-operation:${actorId}:${bookingId}`;
    try { pending = JSON.parse(localStorage.getItem(storageKey)); } catch (_) { /* unavailable storage */ }
    if (pending && (pending.bookingId !== bookingId || !pending.key || !["accept", "reject"].includes(pending.action))) pending = null;
    const publish = () => {
      if (!disposed) setRemote({ bookingId, actorId, snapshot, loading, busy: busy || Boolean(pending), error, anchor, pending });
    };
    const apply = (next, httpStarted) => {
      if (!next || next.bookingId !== bookingId || next.bookingKind !== "BUSINESS") return;
      if (httpStarted != null && next.serverTime) {
        anchor = { server: Number(next.serverTime), monotonic: (httpStarted + performance.now()) / 2 };
      }
      if (!snapshot || Number(next.revision) >= Number(snapshot.revision)) snapshot = next;
      loading = false;
      publish();
    };
    const refresh = async () => {
      if (reading || disposed) return;
      reading = true;
      const started = performance.now();
      try {
        const next = await managedOffersRequest(token, "get", `bookings/BUSINESS/${bookingId}`);
        if (disposed) return;
        error = "";
        apply(next, started);
      } catch (failure) {
        error = errorMessage(failure);
        loading = false;
        publish();
      } finally { reading = false; }
    };
    const clearPending = () => {
      pending = null;
      localStorage.removeItem(storageKey);
    };
    const applyCommandResult = (data) => {
      if (data?.bookingId === bookingId && data.bookingKind === "BUSINESS" &&
          data.assignmentId && data.bookingDetail?.assignmentId === data.assignmentId) {
        apply({ ...data, state: "ASSIGNED", assignment: data.bookingDetail, offers: [] });
      }
    };
    const finishOperation = async (result) => {
      const succeeded = result.status >= 200 && result.status < 300;
      if (succeeded) applyCommandResult(result.data);
      clearPending();
      await refresh();
      if (!succeeded) error = result.message || "La oferta ya no está disponible.";
    };
    const recover = async () => {
      if (!pending || disposed || recovering) return;
      recovering = true;
      try {
        let operation;
        try {
          operation = await managedOffersRequest(token, "get", `operations/${pending.key}`);
        } catch (failure) {
          if (failure.response?.status !== 404) throw failure;
          operation = await managedOffersRequest(token, "post", `operations/${pending.key}/close-if-unseen`);
        }
        if (disposed) return;
        if (operation.state === "COMPLETED" && operation.result) await finishOperation(operation.result);
        else if (operation.state === "CLOSED_NOT_RECEIVED") {
          clearPending();
          error = "La operación no fue recibida. Puedes seleccionar la oferta nuevamente.";
        }
      } catch (_) {
        error = "Verificando el resultado de la operación. Actualiza para volver a comprobarlo.";
      } finally { recovering = false; publish(); }
    };
    const command = async (offer, action) => {
      if (busy || pending || disposed || offer.bookingId !== bookingId) return;
      busy = true;
      error = "";
      pending = { key: uuidv4(), offerId: offer.offerId, action, bookingId };
      // Persist before sending so reloads can recover an uncertain result.
      try { localStorage.setItem(storageKey, JSON.stringify(pending)); }
      catch (_) {
        pending = null;
        busy = false;
        error = "No se pudo guardar la operación. Habilita el almacenamiento del navegador.";
        publish();
        return;
      }
      publish();
      try {
        const result = await managedOffersRequest(token, "post", `${offer.offerId}/${action}`, pending.key);
        if (disposed) return;
        applyCommandResult(result);
        clearPending();
        await refresh();
      } catch (failure) {
        if (disposed) return;
        if (!failure.response || failure.response.status >= 500) await recover();
        else { clearPending(); await refresh(); error = errorMessage(failure); }
      } finally { busy = false; publish(); }
    };
    controller.current = { refresh: async () => { await refresh(); await recover(); }, command };
    publish();
    const start = async () => {
      await refresh();
      await recover();
      if (disposed) return;
      try {
        const session = await managedOffersRequest(token, "post", "session/firebase-token");
        if (disposed) return;
        readApp = initializeApp(db.app.options, `business-offers-${uuidv4()}`);
        await signInWithCustomToken(getAuth(readApp), session.customToken);
        if (disposed) { await deleteApp(readApp); return; }
        const readDb = getDatabase(readApp);
        listeners.push(onValue(ref(readDb, `counterOffersV2/bookings/BUSINESS/${bookingId}`), (value) => apply(value.val()), () => {
          error = "La lectura en tiempo real no está disponible. Actualizando por HTTP.";
          publish();
        }));
        listeners.push(onValue(ref(readDb, ".info/connected"), (value) => { if (value.val()) refresh(); }));
      } catch (_) {
        error = "La lectura en tiempo real no está disponible. Actualizando por HTTP.";
        publish();
      }
    };
    start();
    const poll = setInterval(() => { refresh(); if (!busy) recover(); }, 5000);
    const clock = setInterval(() => setTick((value) => value + 1), 500);
    return () => {
      disposed = true;
      controller.current = null;
      listeners.forEach((unsubscribe) => unsubscribe());
      clearInterval(poll);
      clearInterval(clock);
      if (readApp) deleteApp(readApp).catch(() => {});
    };
  }, [bookingId, token, actorId]);

  const now = current.anchor
    ? current.anchor.server + performance.now() - current.anchor.monotonic
    : Infinity;
  // tick causes expiry to be recalculated without restarting the server deadline.
  void tick;
  const offers = normalizeOffers(current.snapshot, now).map((offer) => ({
    ...offer,
    nameDriver: offer.driver?.name,
    phoneDriver: offer.driver?.phone,
    ...offer.driver,
    priceOffer: offer.price,
    timeArrived: `${Math.ceil(offer.pickupEtaDurationSeconds / 60)} min`,
    secondsLeft: Math.max(0, Math.ceil((offer.expiresAt - now) / 1000)),
  }));
  const assignment = current.snapshot?.assignment;
  const confirmed = matchesAssignment(booking, assignment);
  return {
    offers,
    driver: confirmed ? { ...assignment, idDriver: assignment.driverId } : null,
    loading: current.loading ?? Boolean(bookingId),
    busy: Boolean(current.busy),
    error: current.error || "",
    state: current.snapshot?.state,
    waitingProjection: current.snapshot?.state === "ASSIGNED" && !confirmed,
    refresh: () => controller.current?.refresh(),
    accept: (offer) => controller.current?.command(offer, "accept"),
    reject: (offer) => controller.current?.command(offer, "reject"),
  };
}
